/**
 * Turns a parsed campaign book (`story/book-N-*.md` → `bookN.generated.ts`) into the shapes the
 * campaign already plays: the Tale's chapters (zones) and battles, its comic pages and the lines
 * under them, and the briefs for the pictures it needs drawn. Only FINAL chapters become game
 * content; a draft chapter is a sealed page in the Tale until the owner marks it final.
 *
 * Where a page goes follows from where it sits in the book (`TaleComics`):
 *  - pages before the book's first battle open the Tale (`intro`);
 *  - pages after a battle are the story step told after it (`chapters`);
 *  - the first page after the midpoint battle (the last battle of the second-to-last chapter) is
 *    the twist (`mid`) and must be marked `· twist`; the rest after it are its story step;
 *  - pages after the last battle are the ending: the last is `result`, any before it `beforeResult`.
 *
 * Every check here throws at module load, so a book that cannot be played does not build.
 */
import {
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_ZONE_BOSS,
  node,
  rival,
  type CampaignNode,
  type CampaignZone,
} from '../builders.js';
import { SUIKAMON_SPECIES } from '../../speciesConfig.js';
import type { CastId, ComicPanel } from '../../campaignComics.js';
import type { ComicLine, ComicSpeaker } from '../../campaignComicLines.js';
import type { DialogueScene, StoryRef } from '../storyboard/types.js';
import type { Book, BookBattle, BookPage } from './types.js';

/** The book's names for the cast, where they differ from the cast id (Gran is `aggie` in code). */
const SPEAKER_ID: Record<string, ComicSpeaker> = {
  narrator: 'narrator',
  player: 'player',
  kai: 'player',
  gran: 'aggie',
  timmy: 'timmy',
  cliff: 'cliff',
  vera: 'vera',
  zip: 'zip',
  zap: 'zap',
  zeck: 'zeck',
  gray: 'gray',
};

const XP_BY_KIND: Record<BookBattle['kind'], number> = {
  normal: CAMPAIGN_XP_NORMAL,
  mini_boss: CAMPAIGN_XP_MINI_BOSS,
  zone_boss: CAMPAIGN_XP_ZONE_BOSS,
};

function speciesId(book: Book, name: string): number {
  const found = SUIKAMON_SPECIES.find((s) => s.name.toLowerCase() === name.toLowerCase());
  if (!found) throw new Error(`book ${book.number}: no species called "${name}"`);
  return found.id;
}

/** A cast id as the book writes it (`gran` → `aggie`); species ids pass through. */
const castId = (who: string): CastId => (who.startsWith('sp:') ? (who as CastId) : ((SPEAKER_ID[who] ?? who) as CastId));

export const bookPanel = (p: BookPage): ComicPanel => ({ id: p.id, caption: p.caption, alt: p.alt, cast: p.cast.map(castId) });

export function bookLines(book: Book, p: BookPage): ComicLine[] {
  return p.lines.map((l) => {
    const who = SPEAKER_ID[l.who];
    if (!who) throw new Error(`book ${book.number}, page ${p.id}: unknown speaker "${l.who}"`);
    return { who, text: l.text };
  });
}

const finalChapters = (book: Book) => book.chapters.filter((c) => c.status === 'final');

/** The Tale's chapters, each a zone of its battles, in book order. */
export function bookZones(book: Book): CampaignZone[] {
  return finalChapters(book).map((c) => {
    if (!c.zone) throw new Error(`book ${book.number}, chapter ${c.number}: final but no zone`);
    return {
      zoneId: c.zone.zoneId,
      name: c.zone.name,
      element: c.zone.element,
      story: c.zone.story,
      nodes: c.items.flatMap((it): CampaignNode[] => {
        if (!('battle' in it)) return [];
        const b = it.battle;
        return [
          node({
            nodeId: b.nodeId,
            kind: b.kind,
            encounterKind: b.encounterKind,
            rivalName: b.rivalName,
            rivalTitle: b.rivalTitle,
            story: b.story,
            dialogue: b.dialogue,
            squad: b.squad.map((s) => rival(speciesId(book, s.species), s.nickname || undefined)),
            opponentCount: b.opponentCount,
            xpReward: XP_BY_KIND[b.kind],
            portraitSpeciesId: speciesId(book, b.portrait),
          }),
        ];
      }),
    };
  });
}

export interface BookTale {
  intro: BookPage[];
  chapters: { afterNodeId: string; pages: BookPage[] }[];
  mid: BookPage;
  beforeResult: BookPage[];
  result: BookPage;
}

/** Every final page placed where the Tale plays it (see the file comment). */
export function bookTale(book: Book): BookTale {
  const chapters = finalChapters(book);
  const battlesOf = (i: number) => chapters[i].items.flatMap((it) => ('battle' in it ? [it.battle.nodeId] : []));
  const midBattles = chapters.length >= 2 ? battlesOf(chapters.length - 2) : [];
  const midpoint = midBattles[midBattles.length - 1];
  const items = chapters.flatMap((c) => c.items);
  const lastBattleAt = items.map((it) => 'battle' in it).lastIndexOf(true);
  if (lastBattleAt < 0) throw new Error(`book ${book.number}: no battle in a final chapter`);

  const tale: Partial<BookTale> & Pick<BookTale, 'intro' | 'chapters' | 'beforeResult'> = { intro: [], chapters: [], beforeResult: [] };
  let after: string | null = null;
  items.forEach((it, i) => {
    if ('battle' in it) {
      after = it.battle.nodeId;
      return;
    }
    const page = it.page;
    if (after === null) {
      tale.intro.push(page);
    } else if (i > lastBattleAt) {
      tale.beforeResult.push(page);
    } else if (after === midpoint && !tale.mid) {
      if (!page.twist) throw new Error(`book ${book.number}: page ${page.id} follows the midpoint battle ${midpoint}, so it is the twist: mark it "· twist"`);
      tale.mid = page;
    } else {
      const last = tale.chapters[tale.chapters.length - 1];
      if (last?.afterNodeId === after) last.pages.push(page);
      else tale.chapters.push({ afterNodeId: after, pages: [page] });
    }
    if (page.twist && tale.mid !== page) throw new Error(`book ${book.number}: page ${page.id} is marked twist but does not follow the midpoint battle`);
  });
  const result = tale.beforeResult.pop();
  if (!result) throw new Error(`book ${book.number}: no page after the last battle to end on`);
  if (!tale.mid) throw new Error(`book ${book.number}: no twist page after the midpoint battle ${midpoint ?? '(none)'}`);
  return { ...tale, mid: tale.mid, result };
}

/** Every page of the book's final chapters, in reading order. */
export const bookPages = (book: Book): BookPage[] =>
  finalChapters(book).flatMap((c) => c.items.flatMap((it) => ('page' in it ? [it.page] : [])));

/** Every battle of the book's final chapters, in reading order. */
export const bookBattles = (book: Book): BookBattle[] =>
  finalChapters(book).flatMap((c) => c.items.flatMap((it) => ('battle' in it ? [it.battle] : [])));

/** The battle pictures as dialogue-scene briefs (one per battle, `storyboard/scenes`). */
export const bookScenes = (book: Book): DialogueScene[] =>
  bookBattles(book).map((b) => ({ nodeId: b.nodeId, shot: b.shot, refs: b.refs as StoryRef[], scene: b.brief }));

/** The chapters still being written: shown in the Tale as sealed pages, nothing of them told. */
export const bookDraftChapters = (book: Book): number[] =>
  book.chapters.filter((c) => c.status === 'draft').map((c) => c.number);
